import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  invalid?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const sizeClasses: Record<InputSize, string> = {
  sm: 'h-8 text-caption px-2.5',
  md: 'h-10 text-body px-3',
  lg: 'h-12 text-body-lg px-3.5',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { inputSize = 'md', invalid = false, leadingIcon, trailingIcon, className, ...rest },
  ref,
) {
  if (leadingIcon || trailingIcon) {
    return (
      <span
        className={cn(
          'relative inline-flex w-full items-center rounded-lg border bg-card transition-colors duration-press ease-out-strong',
          invalid
            ? 'border-crit focus-within:border-crit'
            : 'border-line-2 focus-within:border-line-3',
          'focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-2 focus-within:ring-offset-canvas',
          className,
        )}
      >
        {leadingIcon ? (
          <span className="pointer-events-none flex shrink-0 pl-2.5 text-t3">{leadingIcon}</span>
        ) : null}
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          className={cn(
            'w-full flex-1 bg-transparent text-t1 placeholder:text-t3 outline-none disabled:cursor-not-allowed disabled:text-t3',
            sizeClasses[inputSize],
            leadingIcon ? 'pl-2' : '',
            trailingIcon ? 'pr-2' : '',
          )}
          {...rest}
        />
        {trailingIcon ? (
          <span className="pointer-events-none flex shrink-0 pr-2.5 text-t3">{trailingIcon}</span>
        ) : null}
      </span>
    );
  }

  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full rounded-lg border bg-card text-t1 placeholder:text-t3 transition-colors duration-press ease-out-strong',
        'outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        'disabled:cursor-not-allowed disabled:text-t3',
        invalid
          ? 'border-crit focus-visible:border-crit'
          : 'border-line-2 focus-visible:border-line-3',
        sizeClasses[inputSize],
        className,
      )}
      {...rest}
    />
  );
});
